// stickyBucketService.js
'use strict';
import { remoteConfig as remoteConfigDebug } from '../resource/debug.js';

import submitStickyBucket from '../action/submitStickyBucket.js';

const log = remoteConfigDebug.extend('log:stickyBucket');
const errorLog = remoteConfigDebug.extend('error:stickyBucket');

const STORAGE_KEY = 'gbStickyBuckets';

let reduxStore = null;
let cachedAssignments = null;

// localStorage can be full or blocked. Keeping a copy in memory means a
// failed write does not hide the bootstrap variations from the SDK.
const readAssignments = () => {
  if (cachedAssignments) return cachedAssignments;

  try {
    cachedAssignments = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch (error) {
    errorLog('failed to read localStorage', { error });

    cachedAssignments = {};
  }

  return cachedAssignments;
};

const writeAssignments = assignments => {
  if (!Object.keys(assignments).length) return;

  cachedAssignments = assignments;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
  } catch (error) {
    errorLog('failed to write localStorage', { error });
  }
};

/**
 * When the SDK stores a variation it calls `saveAssignments`, and we take that
 * chance to submit it. The SDK decides the arguments, so the store is kept here
 * instead of being passed in.
 * @param {object} params
 * @param {object} params.store - Redux store.
 * @returns {void}
 * @example
 * setStore({ store });
 */
const setStore = ({ store } = {}) => {
  reduxStore = store;
};

/**
 * Store the assignments the bootstrap response came with, and send back the
 * ones only this device has.
 * @param {object} params
 * @param {Object} [params.assignments] - `growthbook.assignments` from bootstrap.
 * @returns {void}
 * @example
 * setBootstrapAssignments({ assignments: { 'live-recommendation-v1__0': '1' } });
 */
const setBootstrapAssignments = ({ assignments = {} } = {}) => {
  log('setBootstrapAssignments', { assignments });

  const deviceAssignments = {
    ...readAssignments(),
    ...assignments,
  };

  writeAssignments(deviceAssignments);

  // They can be unsynced because the SDK calls saveAssignments only at the
  // moment it computes a variation, and there may be no token then — not
  // signed in, or before createApp wired the store.
  const hasUnsyncedAssignments = Object.keys(deviceAssignments).some(
    experimentKey => !(experimentKey in assignments)
  );

  if (hasUnsyncedAssignments && reduxStore) {
    reduxStore.dispatch(submitStickyBucket({ assignments: deviceAssignments }));
  }
};

// Plain object, not a subclass of the SDK's native StickyBucketService class:
// loose mode in babel.config.js compiles `super()` to a plain call, throwing
// "Class constructor cannot be invoked without 'new'".
const stickyBucketService = {
  /**
   * Before the SDK decides which variation to show someone, it asks here
   * whether they already have one. Answer with what this device saved earlier,
   * the same answer no matter which identity it asks about.
   * @param {Object} attributes - GrowthBook attributes, e.g. `{ id: '6a70…' }`.
   * @returns {Promise<Object>} `{ 'id||6a70…': { attributeName, attributeValue,
   *   assignments: { 'my-experiment__0': 'variation-b' } } }` — one entry per
   *   attribute, all carrying the same `assignments`.
   * @see https://docs.growthbook.io/app/sticky-bucketing#hybrid-and-custom-implementations
   * @example
   * await stickyBucketService.getAllAssignments({ id: '6a70…' });
   */
  async getAllAssignments(attributes) {
    log('getAllAssignments', { attributes });

    const assignments = readAssignments();

    if (!Object.keys(assignments).length) return {};

    return Object.entries(attributes).reduce(
      (assignmentDocs, [attributeName, attributeValue]) => {
        if (attributeValue) {
          assignmentDocs[`${attributeName}||${attributeValue}`] = {
            attributeName,
            attributeValue,
            assignments,
          };
        }

        return assignmentDocs;
      },
      {}
    );
  },

  /**
   * The SDK calls this as soon as it picks a variation for a user. Keep it on
   * this device, and send it to the backend if the user is signed in.
   * @param {Object} assignmentDoc - The SDK's document. Only its `assignments`
   *   are kept; the identity it names is dropped.
   * @returns {Promise<void>}
   * @see https://docs.growthbook.io/app/sticky-bucketing#hybrid-and-custom-implementations
   */
  async saveAssignments(assignmentDoc) {
    log('saveAssignments', { assignmentDoc });

    const deviceAssignments = {
      ...readAssignments(),
      ...assignmentDoc.assignments,
    };

    writeAssignments(deviceAssignments);

    if (reduxStore) {
      await reduxStore.dispatch(
        submitStickyBucket({ assignments: deviceAssignments })
      );
    }
  },
};

export { setBootstrapAssignments, setStore };
export default stickyBucketService;
